import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { trpc } from '@/trpc/client';

export function CategoryNew(): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [displayName, setDisplayName] = useState('');
  const [classifierHint, setClassifierHint] = useState('');
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<'normal' | 'optout'>('normal');
  const create = trpc.prompts.category.create.useMutation({
    onSuccess: async () => {
      await utils.prompts.category.list.invalidate();
      navigate('/prompts');
    },
  });

  const canEdit = user?.role === 'editor' || user?.role === 'admin';
  const submitDisabled =
    !canEdit ||
    displayName.trim().length === 0 ||
    classifierHint.trim().length === 0 ||
    body.trim().length === 0 ||
    create.isPending;

  return (
    <div className="max-w-3xl">
      <header className="mb-3 flex items-center justify-between">
        <h1 className="text-lg font-semibold">カテゴリ新規追加</h1>
      </header>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (submitDisabled) return;
          create.mutate({ displayName, classifierHint, body, kind });
        }}
      >
        <label className="block">
          <span className="mb-1 block text-sm font-semibold">表示名</span>
          <input
            type="text"
            className="w-full rounded border p-2 text-sm"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
        <fieldset className="block">
          <legend className="mb-1 text-sm font-semibold">カテゴリ種別</legend>
          <label className="mr-4 inline-flex items-center gap-1 text-sm">
            <input
              type="radio"
              name="kind"
              value="normal"
              checked={kind === 'normal'}
              onChange={() => setKind('normal')}
            />
            通常
          </label>
          <label className="inline-flex items-center gap-1 text-sm">
            <input
              type="radio"
              name="kind"
              value="optout"
              checked={kind === 'optout'}
              onChange={() => setKind('optout')}
            />
            案内停止 (該当時に電話/メールヒアリング → OPT_OUT に遷移)
          </label>
        </fieldset>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold">分類用ヒント</span>
          <span className="mb-1 block text-xs text-gray-500">
            このカテゴリにどんな会話が該当するかを短く説明します。AI
            が会話内容と照らし合わせて該当判定に使います。
          </span>
          <textarea
            className="min-h-[80px] w-full rounded border p-2 text-sm"
            value={classifierHint}
            onChange={(e) => setClassifierHint(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold">応答生成プロンプト</span>
          <span className="mb-1 block text-xs text-gray-500">
            該当時に AI への指示として追加されます。LINE にそのまま送信される文ではなく、AI
            の応答内容を制御するための指示文を書いてください
            (例:「料金は『担当者から正式に』と添えて具体額を言わない」)。
          </span>
          <textarea
            className="min-h-[200px] w-full rounded border p-2 font-mono text-sm"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="rounded border px-3 py-1 text-sm"
            onClick={() => navigate('/prompts')}
          >
            キャンセル
          </button>
          <button
            type="submit"
            className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:bg-gray-300"
            disabled={submitDisabled}
          >
            作成
          </button>
        </div>
      </form>
    </div>
  );
}
